import React, { useState } from "react";

/* PACKAGES */
import { motion } from "framer-motion";
import Select from "react-select";

/* MATERIAL TAILWIND COMPONENTS */
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  Checkbox,
  List,
  ListItem,
  ListItemPrefix,
} from "@material-tailwind/react";

/* USER DEFINED COMPONENTS */
import ProductBox from "../../components/others/product";

/* ICONS */
import { IoIosAdd } from "react-icons/io";
import { FiMinus } from "react-icons/fi";

/* SAMPLE DATA */
import bestsellers from "../../data/bestsellers";
import brands from "../../data/brands";
import categories from "../../data/categories";

/* CONTAINER ANIMATION */
const containerAnimation = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 1 },
  },
};

/* DIV ANIMATION */
const animation = {
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
  hidden: {
    opacity: 0,
    y: 20,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
};

/* SORTING OPTIONS */
const sortOptions = [
  { value: "Order: A-Z", label: "Order: A-Z" },
  { value: "Order: A-Z", label: "Order: A-Z" },
  { value: "Price: High-Low", label: "Price: High-Low" },
  { value: "Price: Low-High", label: "Price: Low-High" },
];

const Shop = () => {
  const [filterCategory, setFilterCategory] = useState(true);
  const [filterBrand, setFilterBrand] = useState(false);
  const [filterPrice, setFilterPrice] = useState(false);
  const [filterAvailability, setFilterAvailability] = useState(false);

  /* FILTER CHECKBOX */
  const FilterCheckbox = (props) => {
    return (
      <ListItem className="p-0 rounded-md">
        <label className="flex w-full cursor-pointer items-center p-3">
          <ListItemPrefix className="mr-3">
            <Checkbox
              id="nutritionFilter"
              ripple={false}
              className="hover:before:opacity-0 rounded w-4 h-4"
              containerProps={{ className: "p-0" }}
            />
          </ListItemPrefix>
          <p className="text-sm font-source font-semibold text-headerGrey">
            {props.label}{" "}
            {props.bold ? (
              <span className="font-source font-bold text-black">
                {props.bold}
              </span>
            ) : null}
          </p>
        </label>
      </ListItem>
    );
  };

  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="py-6 xl:py-14 lg:py-14 bg-white"
    >
      <div className="container">
        <div className="grid grid-cols-1 xl:grid-cols-4 lg:grid-cols-4 gap-5">
          {/* PRODUCT FILTERS */}
          <motion.div variants={animation} className="col-span-1">
            <div className="bg-white border border-greyBorder rounded-2xl p-5 xl:p-6 lg:p-6 shadow-xl xl:sticky lg:sticky top-5">
              <div className="mb-5 flex items-center justify-between">
                <h4 className="text-xl font-fraunces-bold tracking-tight text-black">
                  Filters
                </h4>
                <button className="text-xs font-source font-semibold text-primary hover:text-black transition">
                  Clear all
                </button>
              </div>

              {/* SHOP BY CATEGORY */}
              <Accordion
                open={filterCategory}
                animate={{
                  mount: { scale: 1 },
                  unmount: { scale: 0.9 },
                }}
                icon={
                  filterCategory ? (
                    <FiMinus className="w-4 h-4 text-black" />
                  ) : (
                    <IoIosAdd className="w-4 h-4 text-black" />
                  )
                }
              >
                <AccordionHeader
                  className={`text-sm font-source font-bold text-black bg-greyBg bg-opacity-70 border border-greyBorder border-opacity-60 ${
                    filterCategory ? "!rounded-t-lg" : "!rounded-lg"
                  } p-3`}
                  onClick={() => setFilterCategory(!filterCategory)}
                >
                  Shop by category
                </AccordionHeader>
                <AccordionBody className="bg-greyBg bg-opacity-70 border border-t-0 border-greyBorder border-opacity-60 rounded-b-lg p-0">
                  <List>
                    {categories.map((data) => (
                      <React.Fragment key={data.id}>
                        <FilterCheckbox label={data.label} />
                      </React.Fragment>
                    ))}
                  </List>
                </AccordionBody>
              </Accordion>

              {/* SHOP BY BRAND */}
              <Accordion
                open={filterBrand}
                animate={{
                  mount: { scale: 1 },
                  unmount: { scale: 0.9 },
                }}
                icon={
                  filterBrand ? (
                    <FiMinus className="w-4 h-4 text-black" />
                  ) : (
                    <IoIosAdd className="w-4 h-4 text-black" />
                  )
                }
                className="mt-5"
              >
                <AccordionHeader
                  className={`text-sm font-source font-bold text-black bg-greyBg bg-opacity-70 border border-greyBorder border-opacity-60 ${
                    filterBrand ? "rounded-t-lg" : "rounded-lg"
                  } p-3`}
                  onClick={() => setFilterBrand(!filterBrand)}
                >
                  Shop by brand
                </AccordionHeader>
                <AccordionBody className="bg-greyBg bg-opacity-70 border border-t-0 border-greyBorder border-opacity-60 rounded-b-lg p-0">
                  <List>
                    {brands.map((data) => (
                      <React.Fragment key={data.id}>
                        <FilterCheckbox label={data.label} />
                      </React.Fragment>
                    ))}
                  </List>
                </AccordionBody>
              </Accordion>

              {/* PRICE RANGE */}
              <Accordion
                open={filterPrice}
                animate={{
                  mount: { scale: 1 },
                  unmount: { scale: 0.9 },
                }}
                icon={
                  filterPrice ? (
                    <FiMinus className="w-4 h-4 text-black" />
                  ) : (
                    <IoIosAdd className="w-4 h-4 text-black" />
                  )
                }
                className="mt-5"
              >
                <AccordionHeader
                  className={`text-sm font-source font-bold text-black bg-greyBg bg-opacity-70 border border-greyBorder border-opacity-60 ${
                    filterPrice ? "rounded-t-lg" : "rounded-lg"
                  } p-3`}
                  onClick={() => setFilterPrice(!filterPrice)}
                >
                  Price range
                </AccordionHeader>
                <AccordionBody className="bg-greyBg bg-opacity-70 border border-t-0 border-greyBorder border-opacity-60 rounded-b-lg p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-1">
                      <p className="text-xs font-source text-headerGrey">Min</p>
                      <input
                        type="number"
                        placeholder="0"
                        className="mt-1 w-full rounded-md border border-greyBorder bg-white px-3 py-2 text-sm font-source text-black focus:outline-none"
                      />
                    </div>
                    <div className="col-span-1">
                      <p className="text-xs font-source text-headerGrey">Max</p>
                      <input
                        type="number"
                        placeholder="2500"
                        className="mt-1 w-full rounded-md border border-greyBorder bg-white px-3 py-2 text-sm font-source text-black focus:outline-none"
                      />
                    </div>
                  </div>
                  <button className="mt-4 w-full rounded-full bg-primary text-white text-sm font-source font-semibold py-2 hover:bg-black transition">
                    Apply
                  </button>
                </AccordionBody>
              </Accordion>

              {/* AVAILABILITY */}
              <Accordion
                open={filterAvailability}
                animate={{
                  mount: { scale: 1 },
                  unmount: { scale: 0.9 },
                }}
                icon={
                  filterAvailability ? (
                    <FiMinus className="w-4 h-4 text-black" />
                  ) : (
                    <IoIosAdd className="w-4 h-4 text-black" />
                  )
                }
                className="mt-5"
              >
                <AccordionHeader
                  className={`text-sm font-source font-bold text-black bg-greyBg bg-opacity-70 border border-greyBorder border-opacity-60 ${
                    filterAvailability ? "rounded-t-lg" : "rounded-lg"
                  } p-3`}
                  onClick={() => setFilterAvailability(!filterAvailability)}
                >
                  Availability
                </AccordionHeader>
                <AccordionBody className="bg-greyBg bg-opacity-70 border border-t-0 border-greyBorder border-opacity-60 rounded-b-lg p-0">
                  <List>
                    <FilterCheckbox label="In stock" bold="(24)" />
                    <FilterCheckbox label="Low stock" bold="(6)" />
                    <FilterCheckbox label="Pre-order" bold="(3)" />
                  </List>
                </AccordionBody>
              </Accordion>
            </div>
          </motion.div>

          {/* PRODUCTS LIST */}
          <motion.div variants={animation} className="col-span-3">
            <div className="bg-white border border-greyBorder rounded-2xl p-4 xl:p-5 lg:p-5 shadow-sm">
              <div className="flex flex-col xl:flex-row lg:flex-row xl:items-center lg:items-center xl:justify-between lg:justify-between gap-4">
                <div>
                  <h5 className="text-xl xl:text-lg lg:text-lg font-fraunces-bold tracking-tight text-black">
                    Showing 5 products
                  </h5>
                  <p className="text-sm font-source text-headerGrey">
                    Fresh arrivals curated for wholesale reorders.
                  </p>
                </div>
                <div className="w-full xl:w-[18vh] lg:w-[18vh]">
                  <Select
                    options={sortOptions}
                    placeholder="Sort by"
                    classNamePrefix="state"
                    name="sortBy"
                    id="sortBy"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 lg:grid-cols-3 gap-5 mt-5">
              {bestsellers.map((data) => (
                <div className="col-span-1" key={data.id}>
                  <ProductBox
                    image={data.image}
                    name={data.name}
                    ratings={data.ratings}
                    ratingsPeople={data.ratingsPeople}
                    price={data.price}
                    mrpPrice={data.mrpPrice}
                  />
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default Shop;
